// eslint-disable-next-line no-undef
import { createSlice } from "@reduxjs/toolkit";
import { urls } from "config/urls";
import {
  AcceptTermsAction,
  AddAccountAction,
  AuthSwitchAccountNewAction,
  CheckSessionAction,
  GetAccountListAction,
  GetReviewsAction,
  // LoginWebAction,
  LogOutAction,
  RemoveAccountAction,
  SwitchAccountAction,
} from "store/middlewares/session/auth";
import { encryptAndStoreToken } from "utils/CryptoJS";
import LocalstorageService from "utils/localstorage-services";

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    accountList: null,
    accountListLoading: true,
    userRating: null,
    userRatingLoading: false,
    isAuthenticated: false,
    isLoading: true,
    user: null,
    acceptTermsLoading: false,
  },
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.isLoading = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(CheckSessionAction.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(CheckSessionAction.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = action?.payload?.response?.authenticated;
        state.user = action.payload.response;
        if (action.payload.response.token) {
          const userData = {
            ...action.payload.response,
            isRegister: false,
            token: encryptAndStoreToken(action.payload?.response?.token),
            refreshToken: encryptAndStoreToken(
              action.payload?.response?.refreshToken,
            ),
          };
          LocalstorageService.loginUser(userData);
          LocalstorageService.addEmailToOtherInfo(
            action.payload.response.email,
          );
          const alreadyNavigated = sessionStorage.getItem(
            "drive_first_login_done",
          );

          // Skip the first-login redirect when we are on a share link
          const isShareLink =
            window.location.pathname.startsWith("/drive/shareLink/") ||
            window.location.pathname.startsWith("/drive/folder/");

          if (!alreadyNavigated && !isShareLink) {
            sessionStorage.setItem("drive_first_login_done", "true");

            const path =
              action.payload.response?.drive_start_page === "drive"
                ? "mydrive"
                : "home";

            const desired = `/drive/${path}`;
            if (window.location.pathname !== desired) {
              window.location = desired;
            }
          } else if (!alreadyNavigated) {
            // Still mark it so future normal logins don't re-trigger the redirect
            sessionStorage.setItem("drive_first_login_done", "true");
          }
        }
      })
      .addCase(CheckSessionAction.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      // .addCase(LoginWebAction.fulfilled, (state, action) => {
      //   if (action.payload.response.token) {
      //     const userData = {
      //       ...action.payload.response,
      //       token: encryptAndStoreToken(action.payload?.response?.token),
      //       refreshToken: encryptAndStoreToken(
      //         action.payload?.response?.refreshToken,
      //       ),
      //     };
      //     LocalstorageService.loginUser(userData);
      //     LocalstorageService.addEmailToOtherInfo(
      //       action.payload.response.email,
      //     );
      //     const path =
      //       action.payload.response?.drive_start_page === "drive"
      //         ? "mydrive"
      //         : "home";
      //     window.location = `/drive/${path}`;
      //   }
      // })

      // .addCase(LogOutAction.fulfilled, () => {
      //   LocalstorageService.removeFromLocalStorage("bigfoot-drive");
      // })

      .addCase(AcceptTermsAction.pending, (state) => {
        state.acceptTermsLoading = true;
      })
      .addCase(AcceptTermsAction.fulfilled, (state) => {
        state.acceptTermsLoading = false;
        if (state.user) {
          state.user.has_accepted_latest_terms = true;
        }
      })
      .addCase(AcceptTermsAction.rejected, (state) => {
        state.acceptTermsLoading = false;
      })

      .addCase(LogOutAction.fulfilled, (state, action) => {
        const user = JSON.parse(localStorage.getItem("bigfoot-drive"))?.user;
        if (action.payload.response !== null) {
          if (action.payload.clickType === "forgotPassword") {
            LocalstorageService.removeEmailFromOtherInfo(user?.email);
            LocalstorageService.removeFromLocalStorage("bigfoot-drive");
          } else {
            const userData = {
              ...action.payload.response,
              isRegister: false,
              token: encryptAndStoreToken(action.payload?.response?.token),
              refreshToken: encryptAndStoreToken(
                action.payload?.response?.refreshToken,
              ),
            };
            const path =
              action.payload.response?.drive_start_page === "drive"
                ? "mydrive"
                : "home";
            sessionStorage.removeItem("drive_first_login_done");
            LocalstorageService.loginUser(userData);
            LocalstorageService.removeEmailFromOtherInfo(user?.email);
            sessionStorage.removeItem("isDismiss");
            window.location = `/drive/${path}`;
          }
        } else {
          const redirectUrl = `${urls.DRIVE_URL}drive/home`;
          sessionStorage.removeItem("drive_first_login_done");
          const accountLoginUrl = `${urls.ACCOUNT_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
          LocalstorageService.removeEmailFromOtherInfo();
          LocalstorageService.removeFromLocalStorage("bigfoot-drive");
          sessionStorage.removeItem("isDismiss");
          setTimeout(() => {
            window.location.href = accountLoginUrl;
          }, 500);
        }
      })

      // Account List
      .addCase(GetAccountListAction.pending, (state) => {
        state.accountList =
          state.accountList?.accountsOnDevice?.length > 0
            ? state.accountList
            : null;
      })
      .addCase(GetAccountListAction.fulfilled, (state, action) => {
        state.accountListLoading = false;
        state.accountList = action.payload.response;
      })
      .addCase(GetAccountListAction.rejected, (state) => {
        state.accountListLoading = false;
        state.accountList = null;
      })

      .addCase(AddAccountAction.fulfilled, (state, action) => {
        if (action.payload.response.token) {
          const userData = {
            ...action.payload.response,
            isRegister: false,
            token: encryptAndStoreToken(action.payload?.response?.token),
            refreshToken: encryptAndStoreToken(
              action.payload?.response?.refreshToken,
            ),
          };
          LocalstorageService.loginUser(userData);
          LocalstorageService.addEmailToOtherInfo(
            action.payload.response.email,
          );
          const path =
            action.payload.response?.drive_start_page === "drive"
              ? "mydrive"
              : "home";
          window.location = `/drive/${path}`;
        }
      })

      .addCase(SwitchAccountAction.fulfilled, (state, action) => {
        if (action.payload.response.token) {
          const userData = {
            ...action.payload.response,
            isRegister: false,
            token: encryptAndStoreToken(action.payload?.response?.token),
            refreshToken: encryptAndStoreToken(
              action.payload?.response?.refreshToken,
            ),
          };

          sessionStorage.removeItem("isDismiss");
          LocalstorageService.loginUser(userData);
          const path =
            action.payload.response?.drive_start_page === "drive"
              ? "mydrive"
              : "home";
          window.location = `/drive/${path}`;
        }
      })

      // .addCase(RemoveAccountAction.fulfilled, (state, action) => {
      //   state.accountList.accountsOnDevice =
      //     state.accountList.accountsOnDevice?.filter(
      //       (account) => account.id !== action.payload.id,
      //     );
      // });
      .addCase(RemoveAccountAction.fulfilled, (state, action) => {
        LocalstorageService.removeEmailFromOtherInfo(action.meta.arg?.email);
        state.accountList.accountsOnDevice =
          state.accountList.accountsOnDevice?.filter(
            (account) => account?.user?.email !== action.meta.arg?.email,
          );
      })

      // review
      .addCase(GetReviewsAction.pending, (state) => {
        state.userRatingLoading = true;
        state.userRating = null;
      })
      .addCase(GetReviewsAction.fulfilled, (state, action) => {
        state.userRatingLoading = false;
        state.userRating = action.payload.response;
      })
      .addCase(GetReviewsAction.rejected, (state) => {
        state.userRatingLoading = false;
        state.userRating = null;
      })

      .addCase(AuthSwitchAccountNewAction.fulfilled, (state, action) => {
        if (action.payload.response.token) {
          const userData = {
            ...action.payload.response,
            isRegister: false,
            token: encryptAndStoreToken(action.payload?.response?.token),
            refreshToken: encryptAndStoreToken(
              action.payload?.response?.refreshToken,
            ),
          };

          LocalstorageService.loginUser(userData);
          const path =
            action.payload.response?.drive_start_page === "drive"
              ? "mydrive"
              : "home";
          window.location = `/drive/${path}`;
        }
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
